import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Clock,
  TrendingUp,
  Users,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  Bell,
  Target,
  Globe,
  Zap,
  FileText,
  Award,
  Shield,
  Eye,
} from "lucide-react";
import { useEffect } from "react";

const CorporateMarketingLanding = () => {
  useEffect(() => {
    document.title =
      "NationCard™ for Corporate Marketing & Brand Teams - AI-Powered Brand Sentiment Monitoring";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-[97px] pb-16 md:pt-[105px] md:pb-24 overflow-hidden bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/hero-26-1920x480.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/90 via-violet-600/85 to-indigo-600/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Target size={16} className="mr-2" />
                    <span>Trusted by Fortune 500 Brands</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      NationCard™ for{" "}
                      <span className="text-orange-300">
                        Corporate Marketing
                      </span>{" "}
                      & <span className="text-teal-300">Brand Teams</span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Protect and elevate your brand with real-time, AI-powered
                      public sentiment insights.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-orange-300">
                      Stay ahead of reputation risks and capitalize on brand
                      opportunities instantly
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-orange-500 hover:bg-orange-400 text-purple-900 h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href =
                          "/solutions/corporate-marketing")
                      }
                    >
                      Request a Demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      Download Brand Guide
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Globe size={16} />
                      <span>25+ Countries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target size={16} />
                      <span>Real-time Monitoring</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></span>
                      <span>Live Brand Intelligence</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Dashboard Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                          <BarChart3 size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Brand Sentiment
                          </div>
                          <div className="text-white/60 text-sm">
                            360° Dashboard
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Charts */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Brand Health
                        </div>
                        <div className="text-green-400 text-2xl font-bold">
                          94%
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div className="bg-green-400 h-2 rounded-full w-11/12"></div>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Competitor Gap
                        </div>
                        <div className="text-orange-400 text-2xl font-bold">
                          +18%
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          vs industry avg
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Brand Mentions
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Product launch +45%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span>Customer service +12%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <span>Social campaigns +22%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating verification indicator */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">AI Accuracy</div>
                  <div className="text-orange-300 font-bold">99.4%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Challenges Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Your Challenges
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Traditional brand monitoring leaves marketing teams reactive
                instead of proactive
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Reputation Risks Spreading
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Brand reputation issues escalate across platforms before
                    you're aware they exist
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <BarChart3 className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Disparate Feedback Sources
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Fragmented data across markets and channels makes
                    comprehensive analysis impossible
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Clock className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Manual Analysis Delays
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Time-consuming manual sentiment analysis delays critical
                    strategic decisions
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <TrendingUp className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    No Competitive Benchmarking
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Lack of real-time competitive intelligence leaves you blind
                    to market positioning
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How We Strengthen Your Brand Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                How We Strengthen Your Brand
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Transform brand monitoring from reactive damage control to
                proactive brand building
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                    <Globe className="h-8 w-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Global Sentiment Monitoring
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Track brand health across 25+ countries and platforms with
                    real-time sentiment analysis and cultural context insights.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                    <Eye className="h-8 w-8 text-orange-600" />
                  </div>
                  <CardTitle className="text-xl">
                    AI-Driven Text & Image Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Uncover hidden trends in customer feedback and visual
                    content with advanced AI that reads between the lines.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:light-shadow-elevated hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-200 transition-colors">
                    <BarChart3 className="h-8 w-8 text-teal-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Competitive Benchmarking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Compare your sentiment scores against key competitors and
                    identify opportunities to gain market advantage.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Key Benefits & Capabilities
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Bell className="h-6 w-6 text-red-600 flex-shrink-0" />
                <span className="font-medium">
                  Early-warning alerts on emerging reputation issues
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <FileText className="h-6 w-6 text-blue-600 flex-shrink-0" />
                <span className="font-medium">
                  Automated weekly trend reports and dashboards
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Zap className="h-6 w-6 text-purple-600 flex-shrink-0" />
                <span className="font-medium">
                  Seamless API for CRM and BI tool integrations
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg light-shadow-base hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Award className="h-6 w-6 text-orange-600 flex-shrink-0" />
                <span className="font-medium">
                  Flexible pricing tiers for teams of all sizes
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Success Story Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
              <CardHeader>
                <div className="text-center">
                  <Badge className="mb-4 bg-purple-100 text-purple-700">
                    Brand Success Story
                  </Badge>
                  <CardTitle className="text-2xl md:text-3xl text-nation-blue">
                    How Acme Corp Boosted Brand Love by 30%
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      30%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Uplift in positive sentiment
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600 mb-2">
                      5
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Emerging market opportunities identified
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-teal-600 mb-2">
                      72h
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Crisis response time improvement
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="border-purple-600 text-purple-600 hover:bg-purple-50 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                  >
                    Read Full Case Study
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-nation-blue mb-8">
                Trusted by Leading Brands Worldwide
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60 mb-12">
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  TechGiant Corp
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Global Retail Inc
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Financial Leaders
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Consumer Brands Co
                </div>
              </div>

              <Card className="max-w-2xl mx-auto hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8">
                  <blockquote className="text-lg italic text-muted-foreground mb-6">
                    "NationCard™ transformed our brand strategy. Real-time
                    sentiment insights helped us pivot our product launch
                    messaging, resulting in our most successful campaign launch
                    ever. We caught potential issues before they became crises."
                  </blockquote>
                  <div className="text-center">
                    <div className="font-semibold">Sarah Chen</div>
                    <div className="text-sm text-muted-foreground">
                      VP of Brand Strategy
                    </div>
                    <div className="text-sm text-muted-foreground">
                      TechGiant Corp
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Secure Your Brand's Future with NationCard™
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ready to transform brand monitoring from reactive to proactive?
              See how real-time sentiment intelligence can protect and elevate
              your brand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-white text-purple-600 hover:bg-white/90 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Schedule Brand Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default CorporateMarketingLanding;
